import { Controller } from "@hotwired/stimulus"
import type { LightGallerySettings } from "lightgallery/lg-settings"
import type { LightGallery } from "lightgallery/lightgallery"

// lightGallery is the heaviest thing the public menu could load and the least likely to be
// needed: most diners read the card and never tap a photo. So what ships is a click handler, and
// the library arrives on the click that opens it.
//
// That is also why this does not extend `@stimulus-components/lightbox`, which it otherwise
// duplicates: its `connect()` calls `lightGallery()` outright, which puts the import in the
// entrypoint and the download on every visit.
export default class extends Controller<HTMLElement> {
  static values = { options: Object }

  declare readonly optionsValue: LightGallerySettings

  gallery?: LightGallery

  connect() {
    this.element.addEventListener("click", this.#open)
  }

  disconnect() {
    this.element.removeEventListener("click", this.#open)
    this.gallery?.destroy()
  }

  // Only the first click comes through here: lightGallery binds handlers of its own on the items
  // as it initialises. The listener goes before the import is awaited rather than after, so a
  // second click landing while the chunk is still downloading cannot open a second gallery.
  #open = async (event: MouseEvent) => {
    const index = this.#items.findIndex((item) => item.contains(event.target as Node))

    if (index === -1) return

    event.preventDefault()
    this.element.removeEventListener("click", this.#open)

    const { default: lightGallery } = await import("@/utils/lightGallery")

    this.gallery = lightGallery(this.element, {
      thumbnail: false,
      animateThumb: false,
      download: false,
      ...this.optionsValue,
    })

    this.gallery.openGallery(index)
  }

  // The slides lightGallery would collect for itself: whatever its `selector` matches, or the
  // container's own children when it declares none.
  get #items(): Element[] {
    const { selector } = this.optionsValue

    return typeof selector === "string" && selector.length > 0 ? [...this.element.querySelectorAll(selector)] : [...this.element.children]
  }
}
